import { ApiError, describeError, FormError } from '@classgo/app-kit';
import { MENTORIA_MODELS } from '@classgo/shared';
import { Button, Checkbox, Field, Input, Select } from '@classgo/ui';
import { type FormEvent, type ReactNode, useId, useState } from 'react';

import { useT } from '../i18n';
import type { ContractFormValues } from '../lib/types';

/**
 * The commercial terms of a school, one guided sentence under every field (scoping 3.3: every
 * value editable per school for special deals).
 */
export const ContractForm = ({
  initial,
  mode,
  submitLabel,
  onSubmit,
  secondary,
}: {
  initial: ContractFormValues;
  mode: 'create' | 'edit';
  submitLabel: string;
  onSubmit: (values: ContractFormValues) => Promise<void>;
  secondary?: ReactNode;
}) => {
  const t = useT();
  const notesId = useId();
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof ContractFormValues>(key: K, value: ContractFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(values);
    } catch (thrown) {
      setError(
        thrown instanceof ApiError && thrown.issues.length > 0
          ? thrown.issues.map((issue) => `${issue.path}: ${issue.message}`).join(' · ')
          : describeError(t, thrown),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
      <FormError message={error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('contract.field.startsOn')}
          help={t('contract.field.startsOn.help')}
          type="date"
          required
          readOnly={mode === 'edit'}
          value={values.startsOn}
          onChange={(event) => set('startsOn', event.target.value)}
        />
        <Input
          label={t('contract.field.endsOn')}
          help={t('contract.field.endsOn.help')}
          type="date"
          value={values.endsOn}
          onChange={(event) => set('endsOn', event.target.value)}
        />
      </div>
      <Select
        label={t('contract.field.billingCycle')}
        help={t('contract.field.billingCycle.help')}
        value={values.billingCycle}
        onChange={(event) => set('billingCycle', event.target.value as 'monthly' | 'annual')}
        options={[
          { value: 'monthly', label: t('contract.cycle.monthly') },
          { value: 'annual', label: t('contract.cycle.annual') },
        ]}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('contract.field.platformFee')}
          help={t('contract.field.platformFee.help')}
          inputMode="decimal"
          required
          value={values.platformFee}
          onChange={(event) => set('platformFee', event.target.value)}
        />
        <Input
          label={t('contract.field.pricePerStudent')}
          help={t('contract.field.pricePerStudent.help')}
          inputMode="decimal"
          required
          value={values.pricePerStudent}
          onChange={(event) => set('pricePerStudent', event.target.value)}
        />
      </div>
      <Input
        label={t('contract.field.discountPercent')}
        help={t('contract.field.discountPercent.help')}
        inputMode="decimal"
        value={values.discountPercent}
        onChange={(event) => set('discountPercent', event.target.value)}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('contract.field.courtesyStartsOn')}
          help={t('contract.field.courtesy.help')}
          type="date"
          value={values.courtesyStartsOn}
          onChange={(event) => set('courtesyStartsOn', event.target.value)}
        />
        <Input
          label={t('contract.field.courtesyEndsOn')}
          type="date"
          value={values.courtesyEndsOn}
          onChange={(event) => set('courtesyEndsOn', event.target.value)}
        />
      </div>
      <Checkbox
        label={t('contract.field.mentoriaEnabled')}
        description={t('contract.field.mentoriaEnabled.help')}
        checked={values.mentoriaEnabled}
        onChange={(event) => set('mentoriaEnabled', event.target.checked)}
      />
      {values.mentoriaEnabled ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('contract.field.mentoriaPricePerStudent')}
              help={t('contract.field.mentoriaPricePerStudent.help')}
              inputMode="decimal"
              value={values.mentoriaPricePerStudent}
              onChange={(event) => set('mentoriaPricePerStudent', event.target.value)}
            />
            <Input
              label={t('contract.field.mentoriaFamilyPrice')}
              help={t('contract.field.mentoriaFamilyPrice.help')}
              inputMode="decimal"
              value={values.mentoriaFamilyPrice}
              onChange={(event) => set('mentoriaFamilyPrice', event.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('contract.field.mentoriaMonthlyQuota')}
              help={t('contract.field.mentoriaMonthlyQuota.help')}
              inputMode="numeric"
              value={values.mentoriaMonthlyQuota}
              onChange={(event) => set('mentoriaMonthlyQuota', event.target.value)}
            />
            <Select
              label={t('contract.field.mentoriaModelId')}
              help={t('contract.field.mentoriaModelId.help')}
              value={values.mentoriaModelId}
              onChange={(event) => set('mentoriaModelId', event.target.value)}
              options={[
                { value: '', label: t('contract.model.default') },
                ...MENTORIA_MODELS.map((model) => ({ value: model.modelId, label: model.label })),
              ]}
            />
          </div>
        </>
      ) : null}
      {mode === 'edit' ? (
        <Select
          label={t('contract.field.status')}
          value={values.status}
          onChange={(event) => set('status', event.target.value as 'active' | 'ended')}
          options={[
            { value: 'active', label: t('contract.status.active') },
            { value: 'ended', label: t('contract.status.ended') },
          ]}
        />
      ) : null}
      <Field id={notesId} label={t('contract.field.notes')} help={t('contract.field.notes.help')}>
        <textarea
          id={notesId}
          rows={3}
          maxLength={2000}
          value={values.notes}
          onChange={(event) => set('notes', event.target.value)}
          className="rounded-control border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
      </Field>
      <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
        {secondary}
        <Button type="submit" loading={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
